/**
 * @fileoverview Pure utility functions for metadata-driven mutually exclusive field rules.
 *
 * Fields declare exclusivity via their metadata:
 *   details.validation.linked_fields = [{ field: "<slug>", rule: "mutually_exclusive" }]
 *   details.validation.priority      = <number>   // lower number = higher priority
 *
 * Enforcement rule:
 *   A higher-priority field's selected value(s) must not be selectable in any
 *   lower-priority field that is linked to it with rule "mutually_exclusive".
 *   If a conflict exists, the higher-priority field wins and the conflicting value
 *   is removed from the lower-priority field.
 */

/**
 * Extracts an array of raw scalar value strings from a field's current assetValue.
 *
 * Handles all storage formats used by the dynamic form:
 *  - single-select dropdown  → scalar string/number OR {label, value} object
 *  - multi-select dropdown   → array of {label, value} objects
 *
 * @param {any} assetValue - Current form value for the field.
 * @param {string} fieldType - RHF field type ('dropdown' | 'multi-select-dropdown').
 * @returns {string[]} Flat array of currently selected value strings (never null/undefined).
 */
export function getSelectedValues(assetValue, fieldType) {
    if (assetValue == null || assetValue === "" || assetValue === false) return []

    if (fieldType === "multi-select-dropdown") {
        if (!Array.isArray(assetValue)) return []
        return assetValue
            .map((item) => {
                if (item == null) return null
                if (typeof item === "object") return item.value != null ? String(item.value) : null
                return String(item)
            })
            .filter(Boolean)
    }

    // Single-select: value may be stored as a plain scalar (most common after onChange)
    // or as a full {label, value} object (e.g. from server-side initial data).
    if (typeof assetValue === "object" && assetValue !== null) {
        return assetValue.value != null ? [String(assetValue.value)] : []
    }

    return [String(assetValue)]
}

/**
 * Returns a filtered copy of an options list, omitting any option whose value
 * appears in the provided excluded set.
 *
 * @param {Array<{label: string, value: any}>} options - Full source options array.
 * @param {Set<string> | undefined} excludedValues - String-serialised values to omit.
 * @returns {Array<{label: string, value: any}>} Options with excluded entries removed.
 */
export function filterExcludedOptions(options, excludedValues) {
    if (!excludedValues || excludedValues.size === 0) return options
    return options.filter((opt) => !excludedValues.has(String(opt.value)))
}

/**
 * Strips excluded values from a field's current assetValue and returns a cleaned copy.
 *
 * - multi-select-dropdown: removes matching array items, returns remaining array.
 * - dropdown (single-select): returns null when the selected value is excluded.
 *
 * @param {any} assetValue - Current form value for the field.
 * @param {string} fieldType - Field type ('dropdown' | 'multi-select-dropdown').
 * @param {string[]} excludedValues - Value strings to remove.
 * @returns {any} Cleaned value preserving the original format (array / scalar / null).
 */
export function filterCurrentValue(assetValue, fieldType, excludedValues) {
    if (!excludedValues.length || assetValue == null || assetValue === "") return assetValue

    const excludedSet = new Set(excludedValues.map(String))

    if (fieldType === "multi-select-dropdown") {
        if (!Array.isArray(assetValue)) return assetValue
        return assetValue.filter((item) => {
            const v =
                item != null && typeof item === "object"
                    ? String(item.value ?? "")
                    : String(item ?? "")
            return !excludedSet.has(v)
        })
    }

    // Single-select: derive the scalar value for comparison
    const currentVal =
        typeof assetValue === "object" && assetValue !== null
            ? String(assetValue.value ?? "")
            : String(assetValue)

    // Return null so Autocomplete renders the placeholder (no selection)
    return excludedSet.has(currentVal) ? null : assetValue
}

/**
 * Builds a map of field-slug → Set<string> representing which values must be
 * excluded from each field based on what higher-priority linked fields have selected.
 *
 * Algorithm:
 *  For every field F that has linked_fields with rule "mutually_exclusive":
 *    For each linked field L:
 *      If F.priority < L.priority  (F has higher priority)
 *        → add F's selected values to excludedValuesMap[L.slug]
 *
 * Priority convention: lower number = higher priority (1 > 2 > 3 …).
 *
 * @param {Array<{slug: string, details: object, assetValue: any}>} fields
 *   Current snapshot of the assetProperty array from React Hook Form.
 * @returns {Record<string, Set<string>>}
 *   Map where each key is a field slug and the value is the set of values that
 *   must be excluded from that field's options and current selection.
 */
export function buildExcludedValuesMap(fields) {
    /** @type {Record<string, Set<string>>} */
    const map = {}

    // O(1) lookup: slug → field metadata
    const fieldBySlug = Object.fromEntries(fields.map((f) => [f.slug, f]))

    fields.forEach((field) => {
        // These are the only two metadata keys that drive mutual exclusivity.
        // `priority` (number, lower = higher priority) controls which field wins;
        // `linked_fields` (array of { field, rule }) declares the relationship.
        // Both must be present — a field missing either key is silently ignored.
        const priority = field.details?.validation?.priority
        const linkedFields = (field.details?.validation?.linked_fields ?? []).filter(
            (lf) => lf.rule === "mutually_exclusive",
        )

        // Skip fields with no exclusivity config or no priority assigned
        if (linkedFields.length === 0 || priority == null) return

        const selectedValues = getSelectedValues(field.assetValue, field.details?.type)
        if (selectedValues.length === 0) return

        linkedFields.forEach(({ field: linkedSlug }) => {
            const linkedField = fieldBySlug[linkedSlug]
            if (!linkedField) return

            const linkedPriority = linkedField.details?.validation?.priority

            // Only enforce exclusion from this field onto linked fields that have
            // lower priority (higher priority number). Skip if linked field is equal
            // or higher priority — that field governs itself.
            if (linkedPriority == null || priority >= linkedPriority) return

            if (!map[linkedSlug]) map[linkedSlug] = new Set()
            selectedValues.forEach((v) => map[linkedSlug].add(v))
        })
    })

    return map
}
